import "./UploadSession.css";

import {
  ANALYTICS_EVENTS,
  FILE_MAX_SIZE,
  FILE_MAX_SIZE_MB,
  GEO_EXT,
  SESSION_STATUS,
} from "../../constants";
import {
  Button,
  Col,
  Form,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { addDangerAlert, addWarningAlert } from "../../actions/alerts";
import { resetUploadInstances, uploadInstances } from "./DatasetFileUploader";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { Upload } from "tus-js-client";
import { jsonContentType } from "../../services/backendRequests";
import { renderFormTemplate } from "./FormTemplate";
import tusUrlStorage from "../../services/tusUrlStorage";
import { useTracking } from "../../analytics";

const Title = ({ contract, site, date }) => {
  const formatted = new Date(date);
  return (
    <div className="mb-3 upload-title">
      <span className="text-secondary">{contract} /</span> <b>{site}</b>
      <span className="float-end">
        {formatted.toLocaleString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
      </span>
    </div>
  );
};

const scalarFiles = new Map();
export default function Session() {
  const { sessionId } = useParams();
  const [session, setSession] = useState();
  const user = useSelector((state) => state.user);

  const [scalarData, setScalarData] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [comment, setComment] = useState("");

  const location = useHistory();
  // `resetFields` is false for the post-save reload, so we don't clobber edits
  // the user may have started typing while the session reloads in the background.
  const fetchSession = useCallback(
    (resetFields = true) => {
      fetch(
        `/api/client/upload_sessions/${sessionId}?include_statistics=true`,
        {
          method: "GET",
          headers: jsonContentType(user.identity),
        }
      )
        .then((res) => res.json())
        .then((data) => {
          setSession(data);
          if (resetFields) {
            setScalarData(data.scalar_data);
            setComment(data.comment);
          }
        })
        .catch(() => location.push("/upload"));
    },
    [location, sessionId, user.identity]
  );

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // The uploader instances survive a failed save so the user can retry, so the
  // listener has to be registered once per instance and not once per attempt,
  // or one cancel triggers as many reloads as there were attempts. `off` needs
  // a stable identity, which the ref gives while keeping the latest closure.
  const fetchSessionRef = useRef(fetchSession);
  fetchSessionRef.current = fetchSession;
  const onCancelAll = useCallback(() => {
    console.log("Upload Cancelled !");
    fetchSessionRef.current();
  }, []);

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [wasLoading, setWasLoading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const finishedLoading = wasLoading && !loading;

  const uploadTemplate = session?.upload_template?.name;

  const [statusChoice, setStatusChoice] = useState();
  // Confirmation modal shown before locking a session as COMPLETE.
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const datasets = session?.upload_datasets || [];
  // Both the tab number and the category written on the files derive from this.
  // Two floors, neither of them reliable alone: the number of rows skips a
  // dataset for good when a row is recorded without files of its own, while
  // `statistics` only counts FINISHED files, so it still returns the previous
  // maximum while tusd runs its post-finish hook and loses a category whose
  // files all went DELETED. Whichever is higher never hands out a `dataset_N`
  // already taken. Non-dataset categories (plot_file, gcp_file…) are ignored.
  const highestDataset = Math.max(
    datasets.length,
    (session?.statistics ?? []).reduce((max, stat) => {
      const number = Number(String(stat.category).replace("dataset_", ""));
      return Number.isInteger(number) && number > max ? number : max;
    }, 0)
  );
  const { trackEvent } = useTracking();
  useEffect(() => {
    if (finishedLoading && !uploadFailed) {
      const timer = setTimeout(() => {
        fetchSession(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [finishedLoading, uploadFailed, fetchSession]);

  window.onbeforeunload = () => {
    if (loading) return "Leaving this page will stop any uploads.";
  };

  const onSubmit = (e) => {
    e.preventDefault();

    //if manually disable by the user
    if (!Boolean(statusChoice)) return;

    // Marking a session COMPLETE locks it for good: ask the user to confirm
    // before doing the upload, since they won't be able to add data afterwards.
    if (statusChoice === "complete") {
      setConfirmCompleteOpen(true);
      return;
    }

    performUpload();
  };

  const performUpload = async () => {
    setLoading(true);
    setUploadFailed(false);
    // Local mirror of the uploadFailed state: setUploadFailed() does not update
    // the variable inside this run, so the state cannot be read back here.
    let failed = false;

    const files = Array.from(scalarFiles.values());
    const datasets = Array.from(uploadInstances.values());

    let nbFilesIntoDataset = 0;
    //files.length single file upload do not impact upload session status update

    for (const dataset of datasets) {
      const { files: datasetFiles } = dataset.getState();
      nbFilesIntoDataset += Object.keys(datasetFiles).length;
    }

    const newStatus =
      nbFilesIntoDataset > 0
        ? SESSION_STATUS.InProgress
        : statusChoice === "partial"
        ? SESSION_STATUS.Partial
        : SESSION_STATUS.Completed;

    const sessionRes = await fetch(`/api/client/upload_sessions/${sessionId}`, {
      method: "PUT",
      body: JSON.stringify({
        comment,
        scalar_data: scalarData,
        status: newStatus,
      }),
      headers: jsonContentType(user.identity),
    });

    // The backend may reject the update (validation, locked session, …). Bail
    // out before touching uploads/local state so we don't fake a successful save.
    if (!sessionRes.ok) {
      setLoading(false);
      dispatch(
        addDangerAlert("Could not save the upload session. Please try again.")
      );
      return;
    }

    for (const file of files) file.start();

    for (const dataset of datasets) {
      const { files: datasetFiles } = dataset.getState();

      dataset.off("cancel-all", onCancelAll);
      dataset.on("cancel-all", onCancelAll);

      if (Object.keys(datasetFiles).length === 0) continue;

      // A file already sent in this session resumes as complete after a single
      // HEAD, without transferring anything, so it never emits upload-progress.
      // Counting the ones that do is what tells a real upload from a no-op.
      const transferred = new Set();
      const trackTransfer = (file) => transferred.add(file.id);
      dataset.on("upload-progress", trackTransfer);

      const result = await dataset.upload();
      dataset.off("upload-progress", trackTransfer);

      // Uppy writes totalProgress through a 500ms throttle, so reading it right
      // after upload() resolves still returns the previous value and reports a
      // failure on a successful upload. The result object is authoritative.
      if (result?.failed?.length) {
        failed = true;
        setUploadFailed(true);
      }

      // What really left our machine: `transferred` only says a file emitted
      // progress, so one that started and then failed is still in there.
      const sent = (result?.successful ?? []).filter((file) =>
        transferred.has(file.id)
      );

      // Nothing new went through: creating a dataset here would bump the dataset
      // number and show a row holding no file of its own. The warning is for the
      // redundant-files case only, a total failure is already reported by the
      // form and would be contradicted by it.
      if (sent.length === 0) {
        if (!result?.failed?.length)
          dispatch(
            addWarningAlert(
              "These files were already uploaded in this session: no new dataset was created."
            )
          );
        continue;
      }

      const s3Key = "";

      // Create dataset
      const datasetRes = await fetch(
        `/api/client/upload_sessions/${sessionId}/upload_datasets`,
        {
          method: "POST",
          body: JSON.stringify({
            // What was really sent, not what was selected: a partly redundant
            // folder creates a dataset for its new files only, and a partial
            // failure counts the files tusd actually finished.
            file_count: sent.length,
            root_s3_key: s3Key,
          }),
          headers: jsonContentType(user.identity),
        }
      );

      // A failed dataset creation means the upload isn't fully registered:
      // flag it so the form stays editable and the success reload is skipped.
      if (!datasetRes.ok) {
        failed = true;
        setUploadFailed(true);
        dispatch(
          addDangerAlert(
            "Could not register an uploaded dataset. Please try again."
          )
        );
      }
    }
    setLoading(false);
    setWasLoading(true);
    // Reflect the new status locally right away: a COMPLETE/SYNCHRONIZED save
    // locks the form immediately (no window to re-submit), while PARTIAL /
    // IN_PROGRESS keep it editable.
    setSession((s) => (s ? { ...s, status: newStatus } : s));
    // Only drop the selected files once everything went through: clearing them
    // after a failure leaves the "please retry" message with nothing to retry,
    // and the user has to pick every file again.
    if (!failed) {
      resetUploadInstances();
      scalarFiles.clear();
    }

    trackEvent(ANALYTICS_EVENTS.UPLOAD_SAVE);
  };

  const confirmComplete = () => {
    setConfirmCompleteOpen(false);
    performUpload();
  };

  return (
    <div className="page-content container-fluid upload-session">
      {session ? (
        <Form onSubmit={onSubmit}>
          <Title
            contract={session?.contract_info.name}
            site={session?.site?.display_name}
            date={session?.acquisition_date}
          />

          {renderFormTemplate(
            uploadTemplate,
            {
              datasets,
              data: scalarData,
              fileErrors,
              onChange: (e) => {
                if (e.type === "file") {
                  // Reject an invalid file and drop any previously accepted file
                  // for this field, so a stale name/Upload can't survive the red flag.
                  const rejectFile = (message) => {
                    dispatch(addWarningAlert(message));
                    e.event.target.value = "";
                    scalarFiles.delete(e.key);
                    setScalarData((s) => {
                      const next = { ...s };
                      delete next[e.key];
                      return next;
                    });
                    setFileErrors((s) => ({ ...s, [e.key]: true }));
                  };

                  const fileName = e.data[e.key].split(/[\\/]/).pop();
                  if (!fileName) return;

                  if (e.event.target.files[0].size > FILE_MAX_SIZE) {
                    rejectFile(
                      `This file is too big ! File size must be under ${FILE_MAX_SIZE_MB}MB`
                    );
                    return;
                  }

                  const ext = fileName.split(".").pop();
                  const ext_auth = GEO_EXT[e.key];
                  if (ext_auth && !ext_auth.includes("." + ext)) {
                    rejectFile(
                      "File type (." +
                        ext +
                        ") not accepted. Accepted extensions: " +
                        ext_auth.join(", ")
                    );
                    return;
                  }

                  setFileErrors((s) => ({ ...s, [e.key]: false }));
                  setScalarData((s) => ({ ...s, [e.key]: fileName }));
                  const file = e.event.target.files[0];

                  scalarFiles.set(
                    e.key,
                    new Upload(file, {
                      // Endpoint is the upload creation URL from your tus server
                      endpoint: process.env.REACT_APP_TUS_URL || "",
                      // Retry delays will enable tus-js-client to automatically retry on errors
                      retryDelays: [0, 3000, 5000, 10000, 20000],
                      // Attach additional meta data about the file for the server
                      metadata: {
                        filename: file.name,
                        filetype: file.type,
                        session_id: sessionId,
                        user_id: user.id,
                        category: e.key,
                        scalar: "true",
                      },
                      headers: user.identity,
                      // Same storage as the dataset uploader, so nothing resumable
                      // lives in localStorage any more.
                      urlStorage: tusUrlStorage,
                      fingerprint: (file, options) => {
                        return Promise.resolve(
                          [
                            "tus-br",
                            sessionId,
                            file.name,
                            file.type,
                            file.size,
                            file.lastModified,
                            options?.endpoint,
                          ].join("-")
                        );
                      },
                    })
                  );
                } else {
                  setScalarData((s) => ({ ...s, ...e.data }));
                }
              },
              session,
              comment: comment ?? "",
              setComment,
              status: statusChoice ?? "",
              setStatus: setStatusChoice,
              wasLoading,
              uploadFailed,
              user,
            },
            highestDataset,
            loading,
            statusChoice === "partial"
              ? SESSION_STATUS.Partial
              : SESSION_STATUS.Completed
          )}
        </Form>
      ) : (
        <div>Loading session information.</div>
      )}

      <Modal
        isOpen={confirmCompleteOpen}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <ModalHeader>Mark this session as complete?</ModalHeader>

        <ModalBody>
          <Row>
            <Col xs="12">
              <h5 className="px-2">
                Please, mark as COMPLETE when:
                <ul>
                  <li>All your files are added</li>
                  <li>The session information is provided</li>
                  <li>You made sure that everything is correct</li>
                </ul>
              </h5>
              <div className="alert alert-danger px-2 text-center" role="alert">
                Once saved as COMPLETE, this session is locked for processing,
                and you will no longer be able to add or change any data.
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs="6">
              <Button
                color="primary"
                className="w-100"
                onClick={confirmComplete}
              >
                Mark as COMPLETE
              </Button>
            </Col>
            <Col xs="6">
              <Button
                color="secondary"
                className="w-100"
                onClick={() => setConfirmCompleteOpen(false)}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </div>
  );
}
